import React, {useState} from 'react';
import {injectIntl} from 'react-intl';
import GenericModal from './generic_modal';
import Button from '../../forecast-app/shared/components/buttons/button/button';
import {BUTTON_STYLE, BUTTON_COLOR, ELEMENT_TYPE, account_plan} from '../../constants';
import HeaderBar from '../../forecast-app/shared/components/headers/header-bar/header_bar';
import * as tracking from '../../tracking';
import Util from '../../forecast-app/shared/util/util';
import UploadingOverlay from '../../forecast-app/shared/components/uploading-overlay/uploading_overlay';
import UpdateCompanySubscriptionMutation from '../../mutations/update_company_subscription_mutation';

const plans = {
	PROFESSIONAL: 'PROFESSIONAL',
	PREMIUM: 'PREMIUM',
	ENTERPRISE: 'ENTERPRISE',
};

const billingPeriods = {
	MONTHLY: 'MONTHLY',
	YEARLY: 'YEARLY',
};

const UpgradeSubscriptionModal = ({companyId, country, closeModal, intl, subscription}) => {
	const [paymentInterval, setPaymentInterval] = useState(subscription.plan.includes(billingPeriods.YEARLY) ? billingPeriods.YEARLY : billingPeriods.MONTHLY);
	const [currentPlan, setCurrentPlan] = useState(subscription.plan);
	const [loading, setLoading] = useState(false);
	const onSelectedPaymentIntervalChange = interval => {
		setPaymentInterval(interval);
	};
	const isCurrentPlanLowerThanSelected = plan => {
		return plan === plans.PROFESSIONAL && !currentPlan.includes(plan);
	};

	const getActiveCoupon = plan => {
		const activeCoupon = subscription.coupons ? subscription.coupons[0] : null;

		let coupon = {
			couponId: activeCoupon !== null ? activeCoupon.id : null,
			couponText: activeCoupon !== null ? '' : '',
			percentOff: activeCoupon !== null ? activeCoupon.percentOff : '',
		};

		return plan === plans.ENTERPRISE ? null : coupon;
	};

	const processPlanUpdate = plan => {
		setLoading(true);
		let newPlan;
		if (plan === plans.PROFESSIONAL) {
			newPlan = paymentInterval === billingPeriods.MONTHLY ? account_plan.CHARGEBEE_PROFESSIONAL_MONTHLY : account_plan.CHARGEBEE_PROFESSIONAL_YEARLY;
		}

		if (plan === plans.PREMIUM) {
			newPlan = paymentInterval === billingPeriods.MONTHLY ? account_plan.CHARGEBEE_PREMIUM_MONTHLY : account_plan.CHARGEBEE_PREMIUM_YEARLY;
		}

		const onSuccess = () => {
			setCurrentPlan(newPlan);
			setLoading(false);
		};

		Util.CommitMutation(
			UpdateCompanySubscriptionMutation,
			{
				id: companyId,
				plan: newPlan,
				coupon: getActiveCoupon(plan).couponId,
			},
			onSuccess
		);
	};

	const constructHeader = () => {
		const leftContent = [],
			rightContent = [];

		leftContent.push({
			type: ELEMENT_TYPE.TITLE,
			intl: intl,
			textId: 'settings.subscription.upgrade_plan_title',
		});

		rightContent.push({
			type: ELEMENT_TYPE.UNIT_TOGGLE,
			options: [
				{
					text: intl.formatMessage({id: 'common.monthly'}),
					onClick: () => {
						onSelectedPaymentIntervalChange(billingPeriods.MONTHLY);
						tracking.trackElementClicked('Monthly Tab');
					},
					selected: paymentInterval === billingPeriods.MONTHLY,
					dataCy: 'monthly',
				},
				{
					text: intl.formatMessage({id: 'common.annual'}),
					onClick: () => {
						onSelectedPaymentIntervalChange(billingPeriods.YEARLY);
						tracking.trackElementClicked('Annual Tab');
					},
					selected: paymentInterval === billingPeriods.YEARLY,
					dataCy: 'annual',
				},
			],
		});
		return <HeaderBar leftContent={leftContent} rightContent={rightContent} />;
	};

	const constructPlans = () => {
		const billingPeriodChanged = !currentPlan.includes(paymentInterval);
		const constructUpgradeButton = plan => {
			let buttonText;
			let buttonAction;
			let buttonCY;
			if (plan !== plans.ENTERPRISE) {
				if (!currentPlan.includes(plan)) {
					buttonText = intl.formatMessage({id: 'settings_subscription.upgrade'}).toUpperCase();
					buttonAction = () => {
						processPlanUpdate(plan);
					};
					buttonCY = 'upgrade-to-plan-button';
				} else {
					buttonText =
						paymentInterval === billingPeriods.YEARLY
							? intl.formatMessage({id: 'settings_subscription.switch_annual_billing_period'}).toUpperCase()
							: intl.formatMessage({id: 'settings_subscription.switch_monthly_billing_period'}).toUpperCase();
					buttonAction = () => {
						processPlanUpdate(plan);
					};
					buttonCY = 'switch-plan-billing-button';
				}
			} /* else {
				CX Don't need it apparently
				buttonText = intl.formatMessage({id: 'settings_subscription.contact'}).toUpperCase();
				buttonAction = () => {
					window.open('https://app.hubspot.com/meetings/aldo5/upgrade-to-enterprise-meeting-');
					buttonCY = 'upgrade-to-enterprise-button';
				};
			} */
			return <Button cy={buttonCY} className="change-subscription-button" buttonStyle={BUTTON_STYLE.OUTLINE} colorTheme={BUTTON_COLOR.PURPLE} text={buttonText} onClick={buttonAction} />;
		};

		const getActivePlanIndex = () => {
			for (let [index, value] of Object.values(plans).entries()) {
				if (currentPlan.includes(value)) {
					return index;
				}
			}
		};
		const isPlanActive = plan => currentPlan.includes(plan);
		const getPlanNameId = plan => {
			switch (plan) {
				case plans.PROFESSIONAL:
					return 'professional_plan_name';
				case plans.PREMIUM:
					return 'premium_plan_name';
				case plans.ENTERPRISE:
					return 'enterprise_plan_name';
				default:
					return null;
			}
		};

		const getPlanFeatures = plan => {
			if (plan !== plans.PROFESSIONAL) {
				return (
					<ul>
						<li>
							<span>+</span> {intl.formatMessage({id: 'common.feature_retainers'})}
						</li>
						<li>
							<span>+</span> {intl.formatMessage({id: 'common.feature_premium_integrations'})}
						</li>
						<li>
							<span>+</span> {intl.formatMessage({id: 'common.feature_single_sign_on'})}
						</li>
						<li>
							<span>+</span> {intl.formatMessage({id: 'common.feature_complience'})}
						</li>
						{plan === plans.ENTERPRISE ? (
							<li>
								<span>+</span> {intl.formatMessage({id: 'common.feature_custom_sla'})}
							</li>
						) : null}
					</ul>
				);
			}
		};

		const getTotalSeatPrice = plan => {
			const userPriceMap = Util.getUserSeatPriceMap(subscription.userSeats, plan, paymentInterval);

			const userPriceMapEntries = Util.getUserSeatPriceMapEntries(userPriceMap);

			const totalSeatPriceMonthly = userPriceMapEntries.reduce((acc, elem) => elem.val * elem.key + acc, 0);

			return totalSeatPriceMonthly;
		};

		const getTotalVirtualSeatPrice = () => {
			return subscription.virtualSeats * (paymentInterval === billingPeriods.YEARLY ? 2 : 2.5);
		};

		const getDiscountedAmount = plan => {
			const amount = getTotalSeatPrice(plan) + getTotalVirtualSeatPrice();
			return getActiveCoupon(plan).percentOff ? (amount / 100) * getActiveCoupon(plan).percentOff : amount;
		};

		const getDiscount = plan => {
			const amount = getTotalSeatPrice(plan) + getTotalVirtualSeatPrice();
			return getActiveCoupon(plan).percentOff ? (amount / 100) * getActiveCoupon(plan).percentOff : 0;
		};

		const averageSeatPricePerMonth = plan => {
			return getTotalSeatPrice(plan) / subscription.userSeats;
		};

		const getVATAmount = plan => {
			const amount = getTotalSeatPrice(plan) + getTotalVirtualSeatPrice();
			const amountWithDiscount = amount - getDiscount(plan);
			const monthlyVAT = amountWithDiscount * 0.25;
			return monthlyVAT;
		};
		const getTotal = plan => {
			const amount = getTotalSeatPrice(plan) + getTotalVirtualSeatPrice();
			const amountWithDiscount = amount - getDiscount(plan);
			const totalIncludingVAT = amountWithDiscount * (country === 'DK' ? 1.25 : 1);
			return paymentInterval === billingPeriods.YEARLY ? totalIncludingVAT * 12 : totalIncludingVAT;
		};
		return (
			<div className="subscription-plan">
				{Object.keys(plans).map((plan, index) => {
					return getActivePlanIndex() <= index && !isCurrentPlanLowerThanSelected(plan) ? (
						<div data-cy={plan + '-plan'} key={index} className={'plan ' + plan + '-plan ' + (isPlanActive(plan) ? 'active-plan' : 'inactive-plan') + (billingPeriodChanged ? ' switch-available' : '')}>
							<div className="plan-header">
								{isPlanActive(plan) ? intl.formatMessage({id: 'settings.subscription.your_plan'}).toUpperCase() : null}
								{isPlanActive(plan) && billingPeriodChanged
									? currentPlan.includes(billingPeriods.MONTHLY)
										? ' (' + intl.formatMessage({id: 'common.monthly'}).toUpperCase() + ')'
										: ' (' + intl.formatMessage({id: 'common.annual'}).toUpperCase() + ')'
									: null}
							</div>
							<div className="plan-body">
								<div className="plan-title-section">
									<div className="plan-title">{intl.formatMessage({id: 'select_plan.' + getPlanNameId(plan)}).toUpperCase()}</div>
									<div className="price-info">
										<div className="discount">{getActiveCoupon(plan) ? getActiveCoupon(plan).couponText : ''}</div>
										<div className={'seat-price ' + (plan === plans.ENTERPRISE ? 'enterprise-na' : '')}>
											{plan !== plans.ENTERPRISE ? '$' + intl.formatNumber(averageSeatPricePerMonth(plan), {format: 'rounded_two_decimal'}) : 'N/A'}
										</div>
										<div className="seat-month">
											{plan !== plans.ENTERPRISE
												? intl.formatMessage({id: 'select_plan.seat'}).toLowerCase() + '/' + intl.formatMessage({id: 'select_plan.Month'}).toLowerCase()
												: intl.formatMessage({id: 'settings_subscription.sla'})}
										</div>
									</div>
									<div className="change-subscription-button-wrapper">{getActivePlanIndex() < index || billingPeriodChanged ? constructUpgradeButton(plan) : null}</div>
								</div>
								<div className="plan-feature-section">
									<div className="standard-feature">{intl.formatMessage({id: 'settings.subscription.all_standard_features'})}</div>
									{getPlanFeatures(plan)}
								</div>
								{plan !== plans.ENTERPRISE ? (
									<div className="plan-calculation-section">
										<div className="calculator-title">{intl.formatMessage({id: 'settings.subscription.your_plan'})}</div>
										<table>
											<tbody>
												<tr>
													<th>{intl.formatMessage({id: 'settings_subscription.users'}).toUpperCase()}</th>
													<th>{intl.formatMessage({id: 'select_plan.seat'}).toUpperCase()}</th>
													<th>{intl.formatMessage({id: 'settings_subscription.amount'}).toUpperCase()}</th>
												</tr>
												<tr>
													<td>
														{subscription.userSeats} {intl.formatMessage({id: 'settings_subscription.users'})}
													</td>
													<td>${intl.formatNumber(averageSeatPricePerMonth(plan), {format: 'rounded_two_decimal'})}</td>
													<td>${intl.formatNumber(getTotalSeatPrice(plan), {format: 'rounded_two_decimal'})}</td>
												</tr>
												<tr>
													<td>
														{subscription.virtualSeats} {intl.formatMessage({id: 'settings_subscription.virtual_users'})}
													</td>
													<td>${paymentInterval === 'YEARLY' ? '2' : '2.5'}</td>
													<td>${intl.formatNumber(getTotalVirtualSeatPrice(), {format: 'rounded_two_decimal'})}</td>
												</tr>
												{getActiveCoupon(plan) !== null && getActiveCoupon(plan).percentOff > 0 ? (
													<tr className="discount">
														<td>{intl.formatMessage({id: 'project_section.xero_modal_table_discount'})}</td>
														<td>{getActiveCoupon(plan).percentOff}%</td>
														<td>$ -{intl.formatNumber(getDiscountedAmount(plan), {format: 'rounded_two_decimal'})}</td>
													</tr>
												) : null}
												{country === 'DK' ? (
													<tr className="vat">
														<td>VAT</td>
														<td>25%</td>
														<td>$ {intl.formatNumber(getVATAmount(plan), {format: 'rounded_two_decimal'})}</td>
													</tr>
												) : null}
											</tbody>
										</table>
										{paymentInterval === 'YEARLY' ? (
											<div className="subtotal">
												<div>{intl.formatMessage({id: 'settings_subscription_subtotal_monthly'})}</div>
												<div>${intl.formatNumber(getTotal(plan) / 12, {format: 'rounded_two_decimal'})}</div>
											</div>
										) : null}
										<div className="calculator-line"></div>
										<div className="calculator-total">
											<div className="total">
												{intl.formatMessage({id: 'common.total_column'})} {paymentInterval === billingPeriods.YEARLY ? intl.formatMessage({id: 'common.annual'}) : intl.formatMessage({id: 'common.monthly'})}
											</div>
											<div className="amount">${intl.formatNumber(getTotal(plan), {format: 'rounded_two_decimal'})}</div>
										</div>
									</div>
								) : null}
							</div>
						</div>
					) : null;
				})}
			</div>
		);
	};

	const content = () => {
		return (
			<div className="upgrade-subscription-modal">
				{loading ? <UploadingOverlay /> : null}
				<div className="header-container">{constructHeader()}</div>
				<div className="upgrade-subscription-modal-content">{constructPlans()}</div>
			</div>
		);
	};
	return <GenericModal cy="upgrade-subscription-modal" closeModal={closeModal} content={content()} />;
};

export default injectIntl(UpgradeSubscriptionModal);
